<template>
  <DetailPage>
    <template #left-nav>
      <button
        class="link link-main icon-link"
        @click="props.modalInstance.dismiss"
      >
        <Icon icon="left" /> {{ $t(props.data.backLabel || 'GENERAL.BACK') }}
      </button>
    </template>
    <template #central-nav>
      <h2>{{ $t('PUBLISHING.DETAIL.PRODUCTS.SETTINGS_DIALOG.HEADING') }}</h2>
      <CompositionModeBadge :composition-mode="compositionMode" />
    </template>
    <template #content>
      <div v-if="productType" class="product-settings">
        <ProductSettingsImages
          :default-appearance="defaultAppearance"
          :hotspot-config="hotspotConfig"
          :composition="composition"
          :product-type="productType"
        />
        <ConfigSection
          heading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.HEADING"
          subheading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.SELECTION_TEXT"
        >
          <ProductSettingsMPAHotspots
            v-if="hasMultipleDesigns"
            :main-design-id="composition.mainDesign.id"
            :hotspots="productType?.hotspots"
            :hotspot-configs="hotspotConfig"
            :display-hotspot-images="displayHotspotImages"
            @on-default-select="onHotspotSelection"
            @on-hotspot-configs-selection="onHotspotConfigsSelection"
          />
          <ProductSettingsHotspots
            v-else
            :composition="composition"
            :hotspots="productType?.hotspots"
            :selected-hotspot="selectedHotspot"
            :display-hotspot-images="displayHotspotImages"
            @on-default-select="onHotspotSelection"
          />
        </ConfigSection>
        <ConfigSection
          heading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT.ACTIVE_COLORS.HEADING"
          subheading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT.ACTIVE_COLORS.SUBHEADING"
        >
          <ProductSettingsAppearances
            :appearance-ids="productType?.appearanceIds || []"
            :active-appearance-ids="activatedAppearances"
            :default-appearance-id="defaultAppearance"
            @select-default="onSelectDefaultAppearance"
            @change-appearance-activation="onChangeAppearanceActivation"
          />
        </ConfigSection>
        <ProductSettingsFeedback />
        <div class="spacer"></div>
      </div>
    </template>
    <template #right-nav>
      <button class="btn btn-primary" @click="save">
        {{ $t('GENERAL.SAVE') }}
      </button>
    </template>
  </DetailPage>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { useCompositionStore } from 'commons/stores/composition';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import CompositionModeBadge from 'src/app/components/publishing/statusBadges/CompositionModeBadge.vue';
import ConfigSection from 'src/app/components/publishing/ConfigSection.vue';
import compositionCategoryHelper, {
  CompositionSpecificCategoriesTree,
  CompositionSpecificCategoryProductType,
} from 'commons/composition/compositionCategoryHelper';
import compositionHelper from 'commons/composition/compositionHelper';
import {
  narrowHotspotConfigsToProductType,
  seedHotspotConfigsForProductType,
} from 'commons/composition/multiPrintAreaCreation';
import ProductSettingsHotspots from '../ProductSettingsHotspots.vue';
import {
  AdvancedComposition,
  getProductType,
  HotspotConfigs,
} from '@spreadgroup/common-partner-lib/composition';
import ProductSettingsMPAHotspots from '../ProductSettingsMPAHotspots.vue';
import ProductSettingsAppearances from './ProductSettingsAppearances.vue';
import ProductSettingsImages from './ProductSettingsImages.vue';
import ProductSettingsFeedback from '../ProductSettingsFeedback.vue';

interface Props {
  modalInstance: {
    close: () => void;
    dismiss: () => void;
  };
  data: {
    productTypeId: number;
    backLabel: string;
    appearanceId?: number;
  };
}

const props = defineProps<Props>();

const compositionStore = useCompositionStore();
const compositionMode = computed(() => compositionStore.mode);
const composition = computed(
  () => compositionStore.current as AdvancedComposition,
);
const compositionCategories = computed(
  () => compositionStore.categories as CompositionSpecificCategoriesTree,
);

const productType = computed(
  (): CompositionSpecificCategoryProductType | undefined => {
    if (!compositionCategories.value) return undefined;
    return getProductType(
      compositionCategories.value,
      props.data.productTypeId,
    ) as CompositionSpecificCategoryProductType | undefined;
  },
);

const displayHotspotImages = computed(() => {
  const productTypeCategoryIds =
    compositionCategoryHelper.getAllCategoryIdsOfProductType(
      compositionCategories.value,
      props.data.productTypeId,
    );

  const categoryIdsWithHotspotImages = ['GP01', 'GP02', 'GP04', 'GP05', 'GP06'];
  return productTypeCategoryIds.some((id) =>
    categoryIdsWithHotspotImages.includes(id),
  );
});

const templateProduct = computed(() =>
  compositionHelper.getTemplateProductForProductType(
    composition.value,
    props.data.productTypeId,
  ),
);
const appearanceRule = computed(() =>
  compositionHelper.getAppearanceRuleForProductType(
    composition.value,
    props.data.productTypeId,
  ),
);
const hasMultipleDesigns = computed(() =>
  compositionHelper.hasMultipleDesigns(composition.value),
);

const selectedHotspot = ref<string | undefined>(
  productType.value?.defaultHotSpot,
);
const hotspotConfig = ref<HotspotConfigs>({});

const defaultAppearance = ref<number>(
  props.data.appearanceId ||
    templateProduct.value?.templateProduct?.appearanceRestriction
      ?.includedAppearanceIds?.[0] ||
    productType.value?.defaultAppearance ||
    0,
);
const activatedAppearances = ref<number[]>(
  appearanceRule.value?.includedAppearanceIds ||
    productType.value?.appearanceIds ||
    [],
);

const onHotspotSelection = (hotspot: string) => {
  selectedHotspot.value = hotspot;

  if (!hasMultipleDesigns.value) {
    hotspotConfig.value = {
      [hotspot]: [{ designId: composition.value!.mainDesign.id }],
    };
  }
};

const onHotspotConfigsSelection = (newHotspotConfigs: HotspotConfigs) => {
  hotspotConfig.value = newHotspotConfigs;
};

const onSelectDefaultAppearance = (appearanceId: number) => {
  defaultAppearance.value = appearanceId;
};

const onChangeAppearanceActivation = (
  appearanceId: number,
  isActive: boolean,
) => {
  if (isActive) {
    if (!activatedAppearances.value.includes(appearanceId)) {
      activatedAppearances.value.push(appearanceId);
    }
  } else {
    activatedAppearances.value = activatedAppearances.value.filter(
      (id) => id !== appearanceId,
    );
  }
};

const save = async () => {
  await compositionStore.addOrUpdateProductConfig(
    props.data.productTypeId,
    defaultAppearance.value,
    activatedAppearances.value,
    hotspotConfig.value,
  );

  props.modalInstance.close();
};

onMounted(async () => {
  if (!productType.value || !composition.value) return;

  const existingTemplateProduct = templateProduct.value;

  // A per-product config wins; otherwise seed from the composition. Narrowed either way, because
  // stored data can carry two designs in one print area.
  hotspotConfig.value = existingTemplateProduct?.templateProduct?.hotspotConfigs
    ? narrowHotspotConfigsToProductType({
        hotspotConfigs: existingTemplateProduct.templateProduct.hotspotConfigs,
        supportedHotspots: productType.value.hotspots,
      })
    : seedHotspotConfigsForProductType({
        composition: composition.value,
        supportedHotspots: productType.value.hotspots,
        defaultHotspot: productType.value.defaultHotSpot,
      });

  selectedHotspot.value = Object.keys(hotspotConfig.value)[0];
});
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';
@import 'src/scss/constants';

.product-settings {
  margin: 24px 0 0 0;
  padding: 0 24px;
  width: 100%;
}

.spacer {
  height: 24px;
}
</style>
